import type { OverlayContainer } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { ComponentPortal } from '@angular/cdk/portal';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { DOCUMENT } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import { Inject, Injectable, ViewContainerRef } from '@angular/core';

import type { OverlayPosition, ContextMenuController } from '@core/prosemirror-legacy';
import {
  WysiwygComponentPortalService,
  ContextTooltipOverlay,
  ContextTooltipOverlayContainer,
} from '@core/prosemirror-legacy/angular';

import { ContextToolbarPositionStrategy } from './context-toolbar-position-strategy';
import { ContextToolbarComponent } from './context-toolbar.component';
import { ToolbarService } from '../../toolbar/toolbar.service';

@Injectable()
export class ContextToolbarService
  extends WysiwygComponentPortalService<ContextToolbarComponent>
  implements ContextMenuController, OnDestroy
{
  private readonly isContextTooltipOverlayContainer =
    this.overlayContainer instanceof ContextTooltipOverlayContainer;

  protected positionStrategy: ContextToolbarPositionStrategy;

  private overlayContainerElement = this.overlayContainer.getContainerElement();

  constructor(
    overlay: ContextTooltipOverlay,
    vcRef: ViewContainerRef,
    _viewportRuler: ViewportRuler,
    @Inject(DOCUMENT) _document: Document,
    _platform: Platform,
    @Inject(ContextTooltipOverlayContainer)
    private overlayContainer: ContextTooltipOverlayContainer | OverlayContainer,

    private toolbarService: ToolbarService,
  ) {
    super(overlayContainer, overlay);
  }

  public open(rect: OverlayPosition) {
    this.openComponent();
    this.componentRef!.instance.buttons$ = this.toolbarService.contextButtons$;
    this.componentRef!.changeDetectorRef.detectChanges();
    this.updatePosition(rect);
  }

  public openComponent() {
    this.positionStrategy = new ContextToolbarPositionStrategy();
    this.overlayRef = this.overlay.create({
      positionStrategy: <any>this.positionStrategy,
      ...this.overlayConfig,
    });
    this.portalRef = new ComponentPortal(ContextToolbarComponent, this.vcRef);
    this.componentRef = this.overlayRef.attach(this.portalRef);
  }

  public updatePosition(position: OverlayPosition) {
    const { offsetLeft, offsetTop } = this.getOffset();
    const offsetPosition: OverlayPosition = {
      left: position.left - offsetLeft,
      top: position.top - offsetTop,
    };
    return super.updatePosition(offsetPosition);
  }

  public close(): void {
    this.componentRef?.destroy();
    this.componentRef = null;
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  public ngOnDestroy() {
    this.close();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public isOpen() {
    return !!this.componentRef;
  }

  public getElement(): HTMLElement | null {
    return this.componentRef?.location.nativeElement || null;
  }

  private getOffset() {
    if (!this.isContextTooltipOverlayContainer) return { offsetLeft: 0, offsetTop: 0 };

    const { left: offsetLeft, top: offsetTop } =
      this.overlayContainerElement.getBoundingClientRect();
    return { offsetLeft, offsetTop };
  }
}
